import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type {
  ApiEnvelope,
  BrandIdentity,
  BrandIdentityPayload,
  CardTokenizeRequest,
  CardTokenizeResponse,
  LegacyPaymentData,
  LegacyProductType,
  LineLookupResponse,
  OtpStartResponse,
  OtpValidateResponse,
  PaymentStatusKind,
  PaymentStatusResponse,
  Plan,
  RechargeRequest,
  RechargeResponse,
} from '../types/api';
import { getAppHostname } from './assets';

/**
 * Cliente HTTP único da aplicação.
 *
 * Invariantes (ver CLAUDE.md):
 *
 * 1. **Nenhum segredo no cliente.** Toda chamada vai para o middleware
 *    (`REACT_APP_API_URL`), que resolve o tenant pelo header `x-caller-url`,
 *    gera o Bearer token e injeta `X-Auth-Key-Server`. O SPA não conhece
 *    `chave_acesso`, `TOKEN_RECARGA_FACIL` nem credencial de SMS.
 * 2. **O hostname vem de `getAppHostname()`**, em `assets.ts` — este módulo não
 *    lê `window.location` direto. É o identificador da marca para o middleware,
 *    e o ponto único é o que permite o override `REACT_APP_HOSTNAME`.
 * 3. **O OTP é do servidor.** O SPA recebe apenas handles opacos
 *    (`otp_session_token`, `linha_token`); quem decide se a linha foi validada
 *    é a API.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Extrai a melhor mensagem legível de um corpo de erro da API. */
export function extractApiMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const data = body as Record<string, unknown>;

    const error = data.error;
    if (error && typeof error === 'object') {
      const message = (error as Record<string, unknown>).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (typeof error === 'string' && error.trim()) return error;

    for (const key of ['mensagem', 'message'] as const) {
      const value = data[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return fallback;
}

/**
 * Traduz o payload da API (snake_case, herdado das colunas do ERP) para o
 * `BrandIdentity` que a aplicação usa.
 *
 * **Este é o único ponto de tradução.** Sem ele a marca carrega mas não pinta:
 * `primary_color` nunca vira `primaryColor`, o logo não aparece e o rodapé de
 * contato fica vazio.
 *
 * Duas derivações que o middleware Laravel fazia e precisam continuar:
 *
 * - **WhatsApp** — o ERP não guarda um campo próprio: o número sai do
 *   `sac_marca`, só dígitos, com o DDI `55` na frente.
 * - **Site** — a barra final é removida, senão o link exibido fica
 *   `www.marca.com.br/`.
 */
/**
 * Descarta `logo_url` sem nome de arquivo.
 *
 * O ERP monta a URL com `CONCAT('https://erp.nogle.tech/img/logo-marcas/', <coluna>)`,
 * e no SQL Server `CONCAT` trata `NULL` como string vazia: marca sem logo
 * cadastrado devolve só o prefixo, terminando em `/`. Esse valor é *truthy*, e
 * sem esta checagem o cabeçalho renderiza um `<img>` quebrado em vez de cair
 * para o nome fantasia. O legado tinha tratamento equivalente (commit
 * `31d910f`, "removido a exibicao do logo contel quando vazio do erp").
 */
function sanitizeLogoUrl(raw: string | null | undefined): string | null {
  const url = raw?.trim();
  if (!url) return null;

  const semQuery = url.split(/[?#]/)[0];
  const arquivo = semQuery.slice(semQuery.lastIndexOf('/') + 1);

  return arquivo ? url : null;
}

export function toBrandIdentity(payload: BrandIdentityPayload): BrandIdentity {
  const sac = payload.sac_marca?.trim() || null;
  const sacDigits = sac ? sac.replace(/\D/g, '') : '';
  const site = payload.site_marca?.trim() || null;
  const logo = sanitizeLogoUrl(payload.logo_url);

  return {
    id: payload.id ?? undefined,
    slug: payload.recarga_facil_nome ?? undefined,
    // Mesma cadeia do `$brand['nome_fantasia'] ?? $prefix` do SetBrandTheme:
    // sem nome fantasia no ERP, o slug da marca é melhor rótulo que um genérico.
    nomeFantasia: payload.nome_fantasia?.trim() || payload.recarga_facil_nome?.trim() || 'Recarga Fácil',
    logoUrl: logo,
    primaryColor: payload.primary_color?.trim() || null,
    sac,
    site: site ? site.replace(/\/+$/, '') : null,
    whatsapp: sacDigits ? `55${sacDigits}` : null,
    playStoreUrl: payload.app_google?.trim() || null,
    appStoreUrl: payload.app_apple?.trim() || null,
    // `st_recarga_facil` 0 significa Recarga Fácil suspenso para a marca; o
    // BrandProvider transforma isso na tela de indisponível.
    active: Number(payload.st_recarga_facil ?? 1) === 1,
  };
}

/**
 * Base do middleware.
 *
 * O valor de build (`REACT_APP_API_URL`) é o padrão, e o `middlewareUrl` do
 * `settings.json` da marca pode sobrescrevê-lo em runtime — é o mesmo mecanismo
 * do webpresence, e é o que permite que **um único build** atenda homologação e
 * produção: o hostname de HML aponta para o middleware de HML, cujo `ACCESS_KEY`
 * conhece os hostnames `-hml`. Sem isso, um build de produção servido num
 * hostname de homologação manda `x-caller-url` para o middleware de produção,
 * que não conhece aquele hostname, e toda chamada responde 500.
 *
 * Trocar a base é seguro em qualquer momento porque `Requests.http` cria uma
 * instância nova do axios a cada chamada — não existe cliente memoizado com a
 * base antiga. Ainda assim, o `SettingsProvider` aplica a troca antes de liberar
 * a árvore, e `AppRoutes` só renderiza depois que os settings carregam.
 */
let apiBaseURL: string = process.env.REACT_APP_API_URL || '';

export function setApiBaseUrl(url: string | undefined | null): void {
  const trimmed = (url || '').trim();
  if (trimmed) apiBaseURL = trimmed.replace(/\/+$/, '');
}

export function getApiBaseUrl(): string {
  return apiBaseURL;
}

class Requests {
  private get baseURL(): string {
    return apiBaseURL;
  }

  private get http(): AxiosInstance {
    return axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-caller-url': getAppHostname(),
      },
    });
  }

  private async request<TResponse, TBody = unknown>(
    endpoint: string,
    method: 'get' | 'post' = 'get',
    data?: TBody,
    options?: { timeout?: number }
  ): Promise<TResponse> {
    try {
      const response: AxiosResponse<TResponse> = await this.http.request({
        url: endpoint,
        method,
        data: data ?? undefined,
        timeout: options?.timeout,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? 0;
        const body = error.response?.data;
        const isTimeout = error.code === 'ECONNABORTED';
        const message = isTimeout
          ? 'timeout'
          : extractApiMessage(body, error.message || 'Erro na requisição');
        throw new ApiError(message, status, body);
      }
      throw new ApiError('Erro desconhecido na requisição', 0, error);
    }
  }

  /**
   * IP público do cliente.
   *
   * A API exige `ip` na recarga com cartão (`required_if metodo_pagamento=CARTAO`)
   * e o usa na análise antifraude. O backend Laravel preenchia com
   * `$request->ip()`; num SPA não há equivalente, então buscamos o IP da mesma
   * forma que o `link-venda-direta` faz. Falha aqui não bloqueia o pagamento:
   * devolve `null` e a chamada segue sem o campo.
   */
  async currentIp(): Promise<string | null> {
    try {
      const { data } = await axios.get<string>('https://api.ipify.org', { timeout: 4000 });
      return typeof data === 'string' && data.trim() ? data.trim() : null;
    } catch {
      return null;
    }
  }

  // ─── Identidade da marca ─────────────────────────────────────────────────

  /**
   * Identidade da marca corrente.
   *
   * **Sem parâmetro nenhum**: quem diz de qual marca se trata é o header
   * `x-caller-url` (o hostname), que o middleware traduz em token de tenant.
   * A API devolve a identidade da própria marca do token; para um token que
   * não é do tipo MARCA (o `localhost` de desenvolvimento, por exemplo), ela
   * devolve o bloco padrão Contel.
   */
  async brandIdentity(): Promise<BrandIdentity> {
    const payload = await this.request<BrandIdentityPayload>('/recarga-facil/marca');
    return toBrandIdentity(payload);
  }

  // ─── Linha ───────────────────────────────────────────────────────────────

  /** Detalhes da linha. `retorno: false` ⇒ inexistente ou de outra marca. */
  async lineLookup(numero: string): Promise<LineLookupResponse> {
    return this.request<LineLookupResponse>(
      `/linhas/detalhes?numero=${encodeURIComponent(numero)}`
    );
  }

  // ─── OTP ─────────────────────────────────────────────────────────────────

  /** Gera e envia o código por SMS. O código nunca volta para o cliente. */
  async otpStart(numero: string): Promise<OtpStartResponse> {
    return this.request<OtpStartResponse, { numero: string }>(
      '/recarga-facil/otp/enviar',
      'post',
      { numero }
    );
  }

  async otpValidate(params: {
    otp_session_token: string;
    codigo: string;
  }): Promise<OtpValidateResponse> {
    return this.request<OtpValidateResponse, typeof params>(
      '/recarga-facil/otp/validar',
      'post',
      params
    );
  }

  async otpResend(params: { otp_session_token: string }): Promise<OtpStartResponse> {
    return this.request<OtpStartResponse, typeof params>(
      '/recarga-facil/otp/reenviar',
      'post',
      params
    );
  }

  // ─── Planos ──────────────────────────────────────────────────────────────

  async plans(): Promise<Plan[]> {
    const response = await this.request<ApiEnvelope<Plan[]>>('/planos');
    return response.data ?? [];
  }

  // ─── Recarga e pagamento ─────────────────────────────────────────────────

  /**
   * Solicita a recarga.
   *
   * Usa a rota **do Recarga Fácil**, não a `/recarga/solicitar` genérica: é ela
   * que EXIGE o `linha_token` e recusa a operação quando o token está ausente,
   * expirado, é de outro tenant ou aponta para uma linha diferente da do
   * payload. A rota genérica continua atendendo o ERP, o app e o
   * link-venda-direta, que não têm OTP.
   *
   * Timeout de 45 s, o mesmo do AJAX da versão Blade.
   */
  async recharge(payload: RechargeRequest): Promise<RechargeResponse> {
    return this.request<RechargeResponse, RechargeRequest>(
      '/recarga-facil/recarga/solicitar',
      'post',
      payload,
      { timeout: 45000 }
    );
  }

  /**
   * Recarga com **cartão de crédito**.
   *
   * Rota separada de propósito: `/recarga/solicitar` bloqueia
   * `metodo_pagamento: CARTAO` para todo tenant que não seja o de referência
   * 115 — e o bloqueio devolve **500**, porque o método de recusa não existe
   * na classe. A `/recarga-facil/recarga/solicitar` recusa antes de chegar lá,
   * com 422 e `erro: 'metodo_pagamento_invalido'`. O caminho que atende cartão
   * para qualquer marca é o `.../solicitar/cartao`, que exige o
   * `recaptcha_token` com ação `PAYMENT`.
   */
  async rechargeWithCard(payload: RechargeRequest): Promise<RechargeResponse> {
    return this.request<RechargeResponse, RechargeRequest>(
      '/recarga-facil/recarga/solicitar/cartao',
      'post',
      payload,
      { timeout: 45000 }
    );
  }

  /**
   * Consulta de pagamento usada pelo polling do PIX. A API lê do banco, que é
   * populado pelo webhook da Safe2Pay: `data_pagamento` não-nulo ⇒ pago.
   *
   * O `tipo` escolhe a TABELA consultada, e errar aqui confirma o pagamento de
   * outro cliente: `ControleController::consultaPagamento` lê `OPERADORA_RECARGA`
   * por `id_OPERADORA_RECARGA` quando `tipo=ATIVREC`, e `OPERADORA_PEDIDO` por
   * `id_OPERADORA_PEDIDO` quando `tipo=LOGISTICA`. As duas sequências de id são
   * independentes, então o mesmo número existe nas duas tabelas apontando para
   * solicitações diferentes. `ConsultaPagamentoRequest` só aceita esses dois
   * valores.
   *
   * Recarga Fácil (Fluxo B) é sempre recarga ⇒ `ATIVREC`. No Fluxo A o tipo vem
   * do `t` da URL: `R` e `A` são recarga/ativação (`ATIVREC`), `C` é compra, que
   * o `FinanceiroController::pagamento` lê de `OPERADORA_PEDIDO` ⇒ `LOGISTICA`.
   */
  async paymentStatus(
    id: number | string,
    tipo: PaymentStatusKind = 'ATIVREC'
  ): Promise<PaymentStatusResponse> {
    return this.request<PaymentStatusResponse>(
      `/consultaPagamento?tipo=${tipo}&id=${encodeURIComponent(String(id))}`
    );
  }

  /**
   * Tokeniza o cartão (o PCI fica na API; o SPA não guarda o PAN).
   *
   * O corpo NÃO leva o documento do titular: a API resolve o documento no
   * servidor a partir da linha vinculada ao `otp_session_token`. É assim que a
   * checagem "o documento é o do dono da linha validada", que o Laravel fazia
   * com a sessão de servidor, sobrevive à conversão.
   */
  async tokenizeCard(payload: CardTokenizeRequest): Promise<CardTokenizeResponse> {
    return this.request<CardTokenizeResponse, CardTokenizeRequest>(
      '/recarga-facil/cartao/tokenizar',
      'post',
      payload
    );
  }

  // ─── Fluxo A (legado) ────────────────────────────────────────────────────

  /**
   * Dados da página `/renova_plano?t={tipo}&id={id}`, consumida pelo produto
   * Link de Venda Direta. O contrato de entrada e saída é o mesmo desde a
   * versão Laravel.
   */
  async legacyPayment(params: {
    t: LegacyProductType;
    id: string | number;
  }): Promise<LegacyPaymentData[]> {
    const response = await this.request<
      ApiEnvelope<LegacyPaymentData[]>,
      { tipo: LegacyProductType; id: string | number }
    >('/pagamento', 'post', { tipo: params.t, id: params.id });
    return response.data ?? [];
  }
}

const requests = new Requests();

export default requests;
